/**
 * Up-tree game config path resolution for the playground dev-server plugin.
 *
 * Separate from devServerSecurity.ts (which stays free of Node-only imports);
 * uses only fs/path so it stays importable under ts-jest — the plugin itself
 * uses `import.meta.url` and cannot be imported in jsdom tests.
 */

import { existsSync, readFileSync } from 'fs'
import { basename, dirname, join, resolve as resolvePath } from 'path'

/**
 * Walk up from `startDir` looking for `filename`. Returns the first hit, or
 * null. Mirrors the read-side lookup so writes land where reads look.
 */
export function findUpGameConfigPath(startDir: string, filename: string): string | null {
  let current = resolvePath(startDir)
  const visited = new Set<string>()

  while (true) {
    const candidate = join(current, filename)
    if (existsSync(candidate)) {
      return candidate
    }

    if (visited.has(current)) {
      break
    }
    visited.add(current)

    const parent = dirname(current)
    if (parent === current) {
      break
    }
    current = parent
  }

  return null
}

/**
 * Where a game.config.<target>.json write should land. Reads walk UP the tree
 * (see findGameConfigPath in the plugin), so in a monorepo (Vite root
 * `apps/web/`) a pre-existing repo-root config must be overwritten in place —
 * not shadowed by a new file at the Vite root. An existing config up-tree wins;
 * otherwise the write goes to the project root as before.
 */
export function resolveGameConfigWritePath(projectRoot: string, filename: string): string {
  return findUpGameConfigPath(projectRoot, filename) ?? join(projectRoot, filename)
}

/**
 * Suggested display name for an auto-created playground game: the project-root
 * package.json `name`, falling back to the project-root directory basename.
 */
export function resolveSuggestedGameName(projectRoot: string): string {
  try {
    const pkg = JSON.parse(readFileSync(join(projectRoot, 'package.json'), 'utf-8')) as {
      name?: unknown
    }
    if (typeof pkg.name === 'string' && pkg.name.trim().length > 0) {
      return pkg.name.trim()
    }
  } catch {
    // No (or unreadable/invalid) package.json — use the directory name.
  }
  return basename(resolvePath(projectRoot))
}
